import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const TASK_TYPES = ["telegram", "youtube", "download_app", "website_visit"];

const emptyForm = {
  title: "",
  description: "",
  taskType: "telegram",
  link: "",
  videoUrl: "",
  rewardCoin: "",
  image: null,
};

const TasksPage = () => {
  const token = localStorage.getItem("token");

  // View toggle
  const [activeView, setActiveView] = useState("tasks"); // tasks | completions

  // Tasks list state
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  });

  // Modal / form state
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Completions state
  const [completions, setCompletions] = useState([]);
  const [completionsLoading, setCompletionsLoading] = useState(false);
  const [completionsError, setCompletionsError] = useState("");
  const [completionsTaskId, setCompletionsTaskId] = useState("");
  const [completionsUserId, setCompletionsUserId] = useState("");
  const [completionsPage, setCompletionsPage] = useState(1);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      setError("");
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.perPage,
        ...(debouncedSearch && { search: debouncedSearch.trim() }),
        ...(taskTypeFilter && { taskType: taskTypeFilter }),
        ...(activeFilter && { isActive: activeFilter }),
      });

      const res = await fetch(`${baseUrl}/task/list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();

      if (result.status && result.data) {
        setTasks(Array.isArray(result.data) ? result.data : []);
        setPagination((prev) => ({
          ...prev,
          currentPage: result.pagination?.currentPage || 1,
          lastPage: result.pagination?.lastPage || 1,
          perPage: result.pagination?.perPage || prev.perPage,
          total: result.pagination?.total || 0,
        }));
      } else {
        setTasks([]);
        throw new Error(result.message || "Failed to fetch tasks");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.perPage, debouncedSearch, taskTypeFilter, activeFilter, token]);

  useEffect(() => {
    if (!token) return;
    fetchTasks();
  }, [fetchTasks, token]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch completions
  const fetchCompletions = useCallback(async () => {
    setCompletionsLoading(true);
    try {
      setCompletionsError("");
      const params = new URLSearchParams({
        page: completionsPage,
        limit: 10,
        ...(completionsTaskId && { taskId: completionsTaskId.trim() }),
        ...(completionsUserId && { userId: completionsUserId.trim() }),
      });

      const res = await fetch(`${baseUrl}/task/completions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();

      if (result.status && result.data) {
        setCompletions(Array.isArray(result.data) ? result.data : []);
      } else {
        setCompletions([]);
        throw new Error(result.message || "Failed to fetch completions");
      }
    } catch (err) {
      setCompletionsError(err.message);
    } finally {
      setCompletionsLoading(false);
    }
  }, [completionsPage, completionsTaskId, completionsUserId, token]);

  useEffect(() => {
    if (!token || activeView !== "completions") return;
    fetchCompletions();
  }, [token, activeView, fetchCompletions]);

  // Form handlers
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setFormData((prev) => ({ ...prev, image: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = (isEdit = false) => {
    const errors = {};
    if (!formData.title || formData.title.trim().length < 3) {
      errors.title = "Title must be at least 3 characters";
    }
    if (!formData.description || formData.description.trim().length < 3) {
      errors.description = "Description is required";
    }
    if (!TASK_TYPES.includes(formData.taskType)) {
      errors.taskType = "Task type is required";
    }
    if (!formData.link) {
      errors.link = "Link is required";
    }
    if (
      !formData.rewardCoin ||
      isNaN(formData.rewardCoin) ||
      Number(formData.rewardCoin) <= 0
    ) {
      errors.rewardCoin = "Reward coin must be a positive number";
    }
    if (!isEdit && !formData.image) {
      errors.image = "Image is required for new task";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    setEditingTask(null);
    setFormData(emptyForm);
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || "",
      description: task.description || "",
      taskType: task.taskType || "telegram",
      link: task.link || "",
      videoUrl: task.videoUrl || "",
      rewardCoin: task.rewardCoin ?? "",
      image: null,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const buildFormBody = () => {
    const form = new FormData();
    form.append("title", formData.title);
    form.append("description", formData.description);
    form.append("taskType", formData.taskType);
    form.append("link", formData.link);
    form.append("videoUrl", formData.videoUrl);
    form.append("rewardCoin", formData.rewardCoin);
    if (formData.image) form.append("image", formData.image);
    return form;
  };

  const handleAddNew = async () => {
    if (!validateForm(false)) return;
    setSaving(true);
    try {
      const res = await fetch(`${baseUrl}/task/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: buildFormBody(),
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Create failed");

      toast.success("Task created successfully!");
      setShowModal(false);
      setFormData(emptyForm);
      await fetchTasks();
    } catch (err) {
      toast.error(err.message || "Failed to create task");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm(true)) return;
    setSaving(true);
    try {
      const taskId = editingTask.id;
      const res = await fetch(`${baseUrl}/task/edit/${taskId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: buildFormBody(),
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Update failed");

      toast.success("Task updated successfully!");
      setShowModal(false);
      setEditingTask(null);
      setFormData(emptyForm);
      await fetchTasks();
    } catch (err) {
      toast.error(err.message || "Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      const res = await fetch(`${baseUrl}/task/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Delete failed");

      toast.success("Task deleted successfully!");
      await fetchTasks();
    } catch (err) {
      toast.error(err.message || "Failed to delete task");
    }
  };

  const toggleActive = async (id, isActive) => {
    try {
      const endpoint = isActive
        ? `${baseUrl}/task/deactivate/${id}`
        : `${baseUrl}/task/activate/${id}`;
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Update failed");

      setTasks((prev) =>
        prev.map((t) => (t.id === id ? { ...t, isActive: !isActive } : t))
      );
      toast.success(isActive ? "Task deactivated" : "Task activated");
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.lastPage && page !== pagination.currentPage) {
      setPagination((prev) => ({ ...prev, currentPage: page }));
    }
  };

  const resolveImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith("http")) return image;
    return `${baseUrl.replace("/admin", "")}/${image.replace(/^\//, "")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="flex bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Navbar />
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-white">Task Management</h2>
            {activeView === "tasks" && (
              <button
                onClick={openAddModal}
                className="bg-yellow text-black font-semibold px-4 py-2 rounded hover:bg-yellow-500"
              >
                Add Task
              </button>
            )}
          </div>

          {/* View Tabs */}
          <div className="mb-6 border-b border-gray-800">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveView("tasks")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeView === "tasks"
                    ? "border-white text-white"
                    : "border-transparent text-gray-400 hover:text-white hover:border-white"
                }`}
              >
                Tasks
              </button>
              <button
                onClick={() => setActiveView("completions")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeView === "completions"
                    ? "border-white text-white"
                    : "border-transparent text-gray-400 hover:text-white hover:border-white"
                }`}
              >
                Completions
              </button>
            </nav>
          </div>

          {activeView === "tasks" ? (
            <>
              {/* Filters */}
              <div className="mb-6 flex flex-wrap gap-4">
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border border-gray-600 px-3 py-2 rounded bg-gray-700 text-white w-64"
                />
                <select
                  value={taskTypeFilter}
                  onChange={(e) => {
                    setTaskTypeFilter(e.target.value);
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                  }}
                  className="border border-gray-600 px-3 py-2 rounded bg-gray-700 text-white"
                >
                  <option value="">All Types</option>
                  {TASK_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <select
                  value={activeFilter}
                  onChange={(e) => {
                    setActiveFilter(e.target.value);
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                  }}
                  className="border border-gray-600 px-3 py-2 rounded bg-gray-700 text-white"
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              {/* Modal */}
              {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <div className="bg-gray-800 p-6 rounded shadow-lg w-1/2 max-h-[90vh] overflow-y-auto">
                    <h3 className="text-xl font-semibold mb-4 text-white">
                      {editingTask ? "Edit Task" : "Add New Task"}
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <input
                          type="text"
                          name="title"
                          placeholder="Title"
                          value={formData.title}
                          onChange={handleChange}
                          className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                        />
                        {formErrors.title && (
                          <p className="text-red-500 text-sm">{formErrors.title}</p>
                        )}
                      </div>

                      <div className="col-span-2">
                        <textarea
                          name="description"
                          placeholder="Description"
                          value={formData.description}
                          onChange={handleChange}
                          rows={3}
                          className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                        />
                        {formErrors.description && (
                          <p className="text-red-500 text-sm">{formErrors.description}</p>
                        )}
                      </div>

                      <div>
                        <select
                          name="taskType"
                          value={formData.taskType}
                          onChange={handleChange}
                          className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                        >
                          {TASK_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                        {formErrors.taskType && (
                          <p className="text-red-500 text-sm">{formErrors.taskType}</p>
                        )}
                      </div>

                      <div>
                        <input
                          type="number"
                          name="rewardCoin"
                          placeholder="Reward Coin"
                          value={formData.rewardCoin}
                          onChange={handleChange}
                          className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                        />
                        {formErrors.rewardCoin && (
                          <p className="text-red-500 text-sm">{formErrors.rewardCoin}</p>
                        )}
                      </div>

                      <div className="col-span-2">
                        <input
                          type="text"
                          name="link"
                          placeholder="Link (e.g. https://t.me/yourchannel)"
                          value={formData.link}
                          onChange={handleChange}
                          className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                        />
                        {formErrors.link && (
                          <p className="text-red-500 text-sm">{formErrors.link}</p>
                        )}
                      </div>

                      <div className="col-span-2">
                        <input
                          type="text"
                          name="videoUrl"
                          placeholder="Video URL (optional)"
                          value={formData.videoUrl}
                          onChange={handleChange}
                          className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                        />
                      </div>

                      <div className="col-span-2">
                        <input
                          type="file"
                          name="image"
                          accept="image/*"
                          onChange={handleChange}
                          className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                        />
                        {formErrors.image && (
                          <p className="text-red-500 text-sm">{formErrors.image}</p>
                        )}
                        {editingTask?.image && !formData.image && (
                          <img
                            src={resolveImageUrl(editingTask.image)}
                            alt={editingTask.title}
                            className="h-16 w-16 object-cover rounded mt-1"
                          />
                        )}
                      </div>
                    </div>

                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        onClick={() => setShowModal(false)}
                        className="bg-gray-600 text-white px-4 py-2 rounded"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={editingTask ? handleSave : handleAddNew}
                        disabled={saving}
                        className="bg-yellow text-black px-4 py-2 rounded disabled:opacity-50"
                      >
                        {saving ? "Saving..." : editingTask ? "Save Changes" : "Add Task"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Table */}
              {loading && <p className="text-gray-400">Loading tasks...</p>}
              {error && <p className="text-red-500">{error}</p>}
              {!loading && !error && tasks.length === 0 && (
                <p className="text-gray-400">No tasks found</p>
              )}
              {!loading && tasks.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-gray-800 rounded shadow text-left">
                    <thead className="bg-yellow">
                      <tr>
                        <th className="py-2 px-4">Title</th>
                        <th className="py-2 px-4">Type</th>
                        <th className="py-2 px-4">Reward</th>
                        <th className="py-2 px-4">Image</th>
                        <th className="py-2 px-4">Status</th>
                        <th className="py-2 px-4">Created</th>
                        <th className="py-2 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => (
                        <tr
                          key={task.id}
                          className="border-b border-gray-900 hover:bg-gray-700 text-gray-300"
                        >
                          <td className="py-2 px-4">
                            <div className="font-medium text-white">{task.title}</div>
                            <div className="text-xs text-gray-400 truncate max-w-xs">
                              {task.description}
                            </div>
                          </td>
                          <td className="py-2 px-4">{task.taskType}</td>
                          <td className="py-2 px-4">{task.rewardCoin}</td>
                          <td className="py-2 px-4">
                            {task.image ? (
                              <img
                                src={resolveImageUrl(task.image)}
                                alt={task.title}
                                className="h-12 w-12 object-cover rounded"
                              />
                            ) : (
                              <span className="text-gray-400 italic">No Image</span>
                            )}
                          </td>
                          <td className="py-2 px-4">
                            <span
                              className={`px-2 py-1 rounded text-sm ${
                                task.isActive
                                  ? "bg-green-200 text-green-700"
                                  : "bg-red-200 text-red-700"
                              }`}
                            >
                              {task.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="py-2 px-4 text-sm">{formatDate(task.createdAt)}</td>
                          <td className="py-2 px-4 space-x-2 whitespace-nowrap">
                            <button
                              className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                              onClick={() => openEditModal(task)}
                            >
                              Edit
                            </button>
                            <button
                              className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                              onClick={() => handleDelete(task.id)}
                            >
                              Delete
                            </button>
                            <button
                              className={`px-3 py-1 rounded text-white ${
                                task.isActive
                                  ? "bg-gray-600 hover:bg-gray-700"
                                  : "bg-blue-600 hover:bg-blue-700"
                              }`}
                              onClick={() => toggleActive(task.id, task.isActive)}
                            >
                              {task.isActive ? "Deactivate" : "Activate"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 text-gray-300">
                    <p className="text-sm">
                      {pagination.total > 0 ? (
                        <>
                          Showing{" "}
                          <span className="font-medium text-white">
                            {(pagination.currentPage - 1) * pagination.perPage + 1}
                          </span>{" "}
                          to{" "}
                          <span className="font-medium text-white">
                            {Math.min(
                              pagination.currentPage * pagination.perPage,
                              pagination.total
                            )}
                          </span>{" "}
                          of <span className="font-medium text-white">{pagination.total}</span>{" "}
                          results
                        </>
                      ) : (
                        "No results"
                      )}
                    </p>
                    <div className="space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage <= 1}
                        className="px-3 py-1 rounded border border-gray-600 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-sm">
                        Page {pagination.currentPage} of {pagination.lastPage}
                      </span>
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage >= pagination.lastPage}
                        className="px-3 py-1 rounded border border-gray-600 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Completions Filters */}
              <div className="mb-6 flex flex-wrap gap-4">
                <input
                  type="text"
                  placeholder="Filter by Task ID"
                  value={completionsTaskId}
                  onChange={(e) => {
                    setCompletionsTaskId(e.target.value);
                    setCompletionsPage(1);
                  }}
                  className="border border-gray-600 px-3 py-2 rounded bg-gray-700 text-white w-48"
                />
                <input
                  type="text"
                  placeholder="Filter by User ID"
                  value={completionsUserId}
                  onChange={(e) => {
                    setCompletionsUserId(e.target.value);
                    setCompletionsPage(1);
                  }}
                  className="border border-gray-600 px-3 py-2 rounded bg-gray-700 text-white w-48"
                />
              </div>

              {completionsLoading && <p className="text-gray-400">Loading completions...</p>}
              {completionsError && <p className="text-red-500">{completionsError}</p>}
              {!completionsLoading && !completionsError && completions.length === 0 && (
                <p className="text-gray-400">No completions found</p>
              )}
              {!completionsLoading && completions.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-gray-800 rounded shadow text-left">
                    <thead className="bg-yellow">
                      <tr>
                        <th className="py-2 px-4">User</th>
                        <th className="py-2 px-4">Task</th>
                        <th className="py-2 px-4">Type</th>
                        <th className="py-2 px-4">Status</th>
                        <th className="py-2 px-4">Reward</th>
                        <th className="py-2 px-4">Completed At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completions.map((c) => (
                        <tr
                          key={c.id}
                          className="border-b border-gray-900 hover:bg-gray-700 text-gray-300"
                        >
                          <td className="py-2 px-4">{c.userEmail}</td>
                          <td className="py-2 px-4">{c.taskTitle}</td>
                          <td className="py-2 px-4">{c.taskType}</td>
                          <td className="py-2 px-4">
                            <span className="px-2 py-1 rounded text-sm bg-green-200 text-green-700">
                              {c.status}
                            </span>
                          </td>
                          <td className="py-2 px-4">{c.rewardCoin}</td>
                          <td className="py-2 px-4 text-sm">{formatDate(c.completedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="flex items-center justify-end mt-4 space-x-2 text-gray-300">
                    <button
                      onClick={() => setCompletionsPage((p) => Math.max(1, p - 1))}
                      disabled={completionsPage <= 1}
                      className="px-3 py-1 rounded border border-gray-600 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm">Page {completionsPage}</span>
                    <button
                      onClick={() => setCompletionsPage((p) => p + 1)}
                      disabled={completions.length < 10}
                      className="px-3 py-1 rounded border border-gray-600 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TasksPage;
